import { Dialog } from "./dialog";
import { Button } from "../buttons/button";
import { Pokemon } from "prisma-layer";
import { useState, useRef } from "react";

interface ReleaseConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  pokemon:
    | (Pick<Pokemon, "name" | "base_pokemon_name"> & {
        held_item?: { item_name: string } | null;
      })
    | (Pick<Pokemon, "name" | "base_pokemon_name"> & {
        held_item?: { item_name: string } | null;
      })[]
    | null;
  isLoading?: boolean;
}

export const ReleaseConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  pokemon,
  isLoading,
}: ReleaseConfirmDialogProps) => {
  const [holdProgress, setHoldProgress] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const holdTimeoutRef = useRef<number | null>(null);
  const progressIntervalRef = useRef<number | null>(null);

  if (!isOpen || !pokemon) return null;

  const isArray = Array.isArray(pokemon);
  const count = isArray ? pokemon.length : 1;
  const name = isArray
    ? `${count} Pokémon`
    : pokemon.name || pokemon.base_pokemon_name;

  const HOLD_DURATION = 2000; // 2 seconds

  const startHold = () => {
    if (isLoading) return;

    setIsHolding(true);
    setIsShaking(true);
    setHoldProgress(0);

    // Start progress bar animation
    progressIntervalRef.current = setInterval(() => {
      setHoldProgress((prev) => {
        const next = prev + 100 / (HOLD_DURATION / 100); // Update every 100ms
        return next >= 100 ? 100 : next;
      });
    }, 100);

    // Set timeout for completion
    holdTimeoutRef.current = setTimeout(() => {
      setIsHolding(false);
      setIsShaking(false);
      setHoldProgress(100);
      onConfirm();
    }, HOLD_DURATION);
  };

  const endHold = () => {
    setIsHolding(false);
    setIsShaking(false);
    setHoldProgress(0);

    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
      holdTimeoutRef.current = null;
    }

    if (progressIntervalRef.current) {
      clearInterval(progressIntervalRef.current);
      progressIntervalRef.current = null;
    }
  };

  return (
    <Dialog isOpen={isOpen} onClose={onClose}>
      <style>
        {`
          @keyframes shake {
            0% { transform: translateX(-2px); }
            100% { transform: translateX(2px); }
          }
        `}
      </style>
      <div className="bg-red-50 p-6 rounded-lg border-2 border-red-200 max-w-md w-full m-4">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-bold text-red-800 mb-2 ">
            RELEASE POKEMON
          </h2>
          <p className="text-red-700 mb-4">
            You are about to release <strong>{name}</strong>.
          </p>
          <div className="bg-red-100 p-4 rounded-lg mb-6 border border-red-200">
            <p className="text-red-800 font-bold text-lg mb-2">
              THIS ACTION CANNOT BE UNDONE!
            </p>
            <p className="text-red-700 text-sm leading-relaxed">
              {name} will be removed from your inventory forever and can never
              be recovered.
            </p>
            {!isArray && pokemon.held_item && (
              <p className="text-green-700 text-sm mt-3 font-semibold bg-green-100 p-2 rounded border border-green-200">
                Held item "{pokemon.held_item.item_name}" will be returned to
                your inventory.
              </p>
            )}
            {isArray &&
              pokemon.some((p) => p.held_item) && (
                <p className="text-green-700 text-sm mt-3 font-semibold bg-green-100 p-2 rounded border border-green-200">
                  Any held items will be returned to your inventory.
                </p>
              )}
          </div>
          <div className="grid grid-cols-2 gap-4 justify-center w-full">
            <Button
              variant="night"
              onClick={onClose}
              disabled={isLoading ?? false}
              className="flex-0"
            >
              CANCEL
            </Button>
            <div className=" relative ">
              {/* Progress bar */}
              {isHolding && (
                <div className="absolute inset-0 bg-red-800 rounded-md overflow-hidden">
                  <div
                    className="h-full bg-red-600 transition-all duration-100 ease-linear"
                    style={{ width: `${holdProgress}%` }}
                  />
                </div>
              )}

              <div
                className={`relative grow w-full z-10 ${
                  isShaking ? "animate-pulse" : ""
                }`}
                style={{
                  transform: isShaking ? "translateX(-2px)" : "translateX(0)",
                  animation: isShaking
                    ? "shake 0.1s infinite alternate"
                    : "none",
                }}
                onMouseDown={startHold}
                onMouseUp={endHold}
                onMouseLeave={endHold}
                onTouchStart={startHold}
                onTouchEnd={endHold}
              >
                <Button
                  variant="red"
                  disabled={isLoading ?? false}
                  className="flex-1 w-full grow bg-red-600 hover:bg-red-700 border-red-800 text-white shadow-red-900 transition-all duration-200"
                >
                  {isLoading ? "RELEASING..." : "RELEASE"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
